import type { ReactNode } from "react";
import Svg, { Rect } from "react-native-svg";
import { ArrowUpRight as ArrowUpRightIcon } from "@tamagui/lucide-icons";
import { Body3, Card, Display3, Display4, isWeb, Subtitle2, useMedia, XStack, YStack } from "@medbillai/ui";
import { useRouter } from "@medbillai/navigation/router";
import { coverageCardUrl, coveragePlanDetailsUrl, coverageWhatYouPayUrl } from "@medbillai/utils/urls";
import { analyticsService } from "../../../lib/analytics";
import { CareCategoryBadge, careCategoryLabel } from "../careCategories";
import { shownPrice } from "../format";
import type { CoveragePlan, Price } from "../read/readCoverage";
import { PayerMark } from "./PlanHeader";
const TILE_LABEL_TRACKING = -0.15;
export function RootTiles({
  plan
}: {
  plan: CoveragePlan;
}) {
  const {
    rootPrice
  } = plan;
  const media = useMedia();
  if (isWeb && media.gtSm) {
    return <XStack gap="$sp1">
        {rootPrice ? <YStack flex={1} flexBasis={0}>
            <PriceTile price={rootPrice} />
          </YStack> : null}
        <YStack flex={1} flexBasis={0}>
          <CardTile memberId={plan.memberId} artworkAboveLabel />
        </YStack>
        <YStack flex={1} flexBasis={0}>
          <PlanDetailsTile plan={plan} artworkAboveLabel />
        </YStack>
      </XStack>;
  }
  return rootPrice ? <XStack gap="$sp1" data-sentry-element="XStack" data-sentry-component="RootTiles" data-sentry-source-file="RootTiles.tsx">
      <PriceTile price={rootPrice} data-sentry-element="PriceTile" data-sentry-source-file="RootTiles.tsx" />
      <YStack flex={1} gap="$sp1" data-sentry-element="YStack" data-sentry-source-file="RootTiles.tsx">
        <CardTile memberId={plan.memberId} data-sentry-element="CardTile" data-sentry-source-file="RootTiles.tsx" />
        <PlanDetailsTile plan={plan} data-sentry-element="PlanDetailsTile" data-sentry-source-file="RootTiles.tsx" />
      </YStack>
    </XStack> : <XStack gap="$sp1" data-sentry-element="XStack" data-sentry-component="RootTiles" data-sentry-source-file="RootTiles.tsx">
      <YStack flex={1} flexBasis={0} data-sentry-element="YStack" data-sentry-source-file="RootTiles.tsx">
        <CardTile memberId={plan.memberId} data-sentry-element="CardTile" data-sentry-source-file="RootTiles.tsx" />
      </YStack>
      <YStack flex={1} flexBasis={0} data-sentry-element="YStack" data-sentry-source-file="RootTiles.tsx">
        <PlanDetailsTile plan={plan} data-sentry-element="PlanDetailsTile" data-sentry-source-file="RootTiles.tsx" />
      </YStack>
    </XStack>;
}
function PriceTile({
  price
}: {
  price: Price;
}) {
  const shown = shownPrice(price);
  const {
    push
  } = useRouter();
  // A range takes a step down so it fits the tile.
  const Amount = shown?.amount.kind === "range" ? Display4 : Display3;
  return (
    // Sized to its content; the card and plan tiles take the rest.
    <Card variant="raised" flexShrink={0} padding="$sp0" role="button" aria-label={`${[careCategoryLabel[price.category], shown?.text].filter(Boolean).join(" ")}. What you pay, by visit`} onPress={() => {
      analyticsService.trackEvent("CoverageTileTapped", {
        tile: "price"
      });
      push(coverageWhatYouPayUrl());
    }} data-sentry-element="Card" data-sentry-component="PriceTile" data-sentry-source-file="RootTiles.tsx">
      <YStack flex={1} padding="$sp1.5" data-sentry-element="YStack" data-sentry-source-file="RootTiles.tsx">
        <CareCategoryBadge alignSelf="flex-start" size="small" category={price.category} data-sentry-element="CareCategoryBadge" data-sentry-source-file="RootTiles.tsx" />
        <Amount color="$textDefault" marginTop="$sp1" data-sentry-element="Amount" data-sentry-source-file="RootTiles.tsx">
          {shown?.text}
        </Amount>
        <XStack alignItems="center" gap="$sp0.5" marginTop="auto" data-sentry-element="XStack" data-sentry-source-file="RootTiles.tsx">
          <Body3 color="$textSubtle" data-sentry-element="Body3" data-sentry-source-file="RootTiles.tsx">What you pay, by visit</Body3>
          <ArrowUpRightIcon size="$iconXSmall" color="$iconSubtle" data-sentry-element="ArrowUpRightIcon" data-sentry-source-file="RootTiles.tsx" />
        </XStack>
      </YStack>
    </Card>
  );
}
function CardTile({
  memberId,
  artworkAboveLabel = false
}: {
  memberId: string | null;
  artworkAboveLabel?: boolean;
}) {
  const {
    push
  } = useRouter();
  return <Tile artworkAboveLabel={artworkAboveLabel} label="Insurance card" detail={memberId ? `ID ··· ${memberId.slice(-4)}` : null} onPress={() => {
    analyticsService.trackEvent("CoverageTileTapped", {
      tile: "card"
    });
    push(coverageCardUrl());
  }} data-sentry-element="Tile" data-sentry-component="CardTile" data-sentry-source-file="RootTiles.tsx">
      <MiniCard data-sentry-element="MiniCard" data-sentry-source-file="RootTiles.tsx" />
    </Tile>;
}
function PlanDetailsTile({
  plan,
  artworkAboveLabel = false
}: {
  plan: CoveragePlan;
  artworkAboveLabel?: boolean;
}) {
  const {
    push
  } = useRouter();
  return <Tile artworkAboveLabel={artworkAboveLabel} label="Plan details" detail="Benefits & rules" onPress={() => {
    analyticsService.trackEvent("CoverageTileTapped", {
      tile: "planDetails"
    });
    push(coveragePlanDetailsUrl());
  }} data-sentry-element="Tile" data-sentry-component="PlanDetailsTile" data-sentry-source-file="RootTiles.tsx">
      <YStack width="$si4" height="$si4" alignItems="center" justifyContent="center" data-sentry-element="YStack" data-sentry-source-file="RootTiles.tsx">
        <PayerMark name={plan.payerName} logoUrl={plan.logoUrl} size="$si3" data-sentry-element="PayerMark" data-sentry-source-file="RootTiles.tsx" />
      </YStack>
    </Tile>;
}
function Tile({
  label,
  detail,
  onPress,
  artworkAboveLabel,
  children
}: {
  label: string;
  detail: string | null;
  onPress: () => void;
  artworkAboveLabel: boolean;
  children: ReactNode;
}) {
  return <Card variant="raised" flex={1} paddingLeft="$sp1.5" paddingRight="$sp1" paddingVertical="$sp1" aria-label={label} onPress={onPress} data-sentry-element="Card" data-sentry-component="Tile" data-sentry-source-file="RootTiles.tsx">
      <XStack flexDirection={artworkAboveLabel ? "column" : "row"} alignItems={artworkAboveLabel ? "flex-start" : "center"} gap="$sp1" flex={1} data-sentry-element="XStack" data-sentry-source-file="RootTiles.tsx">
        {children}
        <YStack flex={1} data-sentry-element="YStack" data-sentry-source-file="RootTiles.tsx">
          <Subtitle2 color="$textDefault" letterSpacing={TILE_LABEL_TRACKING} data-sentry-element="Subtitle2" data-sentry-source-file="RootTiles.tsx">
            {label}
          </Subtitle2>
          {detail ? <Body3 color="$textSubtle" marginTop="$sp0.5">
              {detail}
            </Body3> : null}
        </YStack>
      </XStack>
    </Card>;
}

/** An illustration: its values are the prototype's own, outside the DS. */
function MiniCard() {
  return <YStack aria-hidden borderRadius={MINI_CARD.radius} boxShadow="0 1px 3px #22381F40" data-sentry-element="YStack" data-sentry-component="MiniCard" data-sentry-source-file="RootTiles.tsx">
      <Svg width={MINI_CARD.width} height={MINI_CARD.height} data-sentry-element="Svg" data-sentry-source-file="RootTiles.tsx">
        <Rect width={MINI_CARD.width} height={MINI_CARD.height} rx={MINI_CARD.radius} fill="#22381F" data-sentry-element="Rect" data-sentry-source-file="RootTiles.tsx" />
        <Rect x={0.5} y={0.5} width={MINI_CARD.width - 1} height={MINI_CARD.height - 1} rx={MINI_CARD.radius - 0.5} fill="none" stroke="#FFFFFF" strokeOpacity={0.12} data-sentry-element="Rect" data-sentry-source-file="RootTiles.tsx" />
        <Rect x={5} y={5} width={8} height={6} rx={2} fill="#C3EF75" data-sentry-element="Rect" data-sentry-source-file="RootTiles.tsx" />
        <Rect x={5} y={17.5} width={18} height={1.5} fill="#FFFFFF" fillOpacity={0.35} data-sentry-element="Rect" data-sentry-source-file="RootTiles.tsx" />
      </Svg>
    </YStack>;
}
const MINI_CARD = {
  width: 34,
  height: 24,
  radius: 6
};